import { useEffect, useState } from 'react';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';
import {
  D365,
  DISCOUNTS,
  ENTRA,
  GW,
  M365,
  SALESFORCE,
} from '../constants/prices';
import { checkForConversions } from '../lib/exceptionalCountries';

/**
 * Calculate the estimated price
 *
 * @param {currency}
 * @param {localeCode}
 * @param {country}
 * @param {m365}
 * @param {entra}
 * @param {salesforce}
 * @param {gw}
 * @param {d365}
 * @param {gw}
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  localeCode: string;
  country: string;
  m365: number;
  entra: number;
  salesforce: number;
  d365: number;
  gw: number;
  storage: boolean;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

const partsToQuery = [
  {
    partNumber: M365.STORAGE,
  },
  {
    partNumber: M365.WH_STORAGE,
  },
  {
    partNumber: ENTRA.STORAGE,
  },
  {
    partNumber: SALESFORCE.STORAGE,
  },
  {
    partNumber: SALESFORCE.WH_STORAGE,
  },
  {
    partNumber: D365.STORAGE,
  },
  {
    partNumber: D365.WH_STORAGE,
  },
  {
    partNumber: GW.STORAGE,
  },
  {
    partNumber: GW.WH_STORAGE,
  },
];

type PricingModel = {
  D0BY8ZX: number;
  D0BYAZX: number;
  D0DGXZX: number;
  D0DGZZX: number;
  D0EQHZX: number;
  D09S8ZX: number;
  D09S9ZX: number;
  D0HDUZX: number;
  D0HDWZX: number;
};

export const usePrice = ({
  currency,
  localeCode,
  country,
  m365,
  entra,
  salesforce,
  d365,
  gw,
  storage,
}: PropsFunc) => {
  const expectional = checkForConversions(country);

  const [totalCost, setTotalCost] = useCurrencyWithSymbol(
    expectional !== undefined ? expectional.publishedCurrency : currency,
    localeCode,
    0,
  );
  const [m365Price, setM365Price] = useCurrencyWithSymbol(
    expectional !== undefined ? expectional.publishedCurrency : currency,
    localeCode,
    0,
  );
  const [entraPrice, setEntraPrice] = useCurrencyWithSymbol(
    expectional !== undefined ? expectional.publishedCurrency : currency,
    localeCode,
    0,
  );
  const [sfPrice, setSFPrice] = useCurrencyWithSymbol(
    expectional !== undefined ? expectional.publishedCurrency : currency,
    localeCode,
    0,
  );
  const [d365Price, setD365] = useCurrencyWithSymbol(
    expectional !== undefined ? expectional.publishedCurrency : currency,
    localeCode,
    0,
  );
  const [gwPrice, setGWPrice] = useCurrencyWithSymbol(
    expectional !== undefined ? expectional.publishedCurrency : currency,
    localeCode,
    0,
  );

  const [m365ListPrice, setM365ListPrice] = useState(0);
  const [entraListPrice, setEntraListPrice] = useState(0);
  const [sfListPrice, setSfListPrice] = useState(0);
  const [d365ListPrice, setD365ListPrice] = useState(0);
  const [gwListPrice, setGwListPrice] = useState(0);
  const [totalCostRaw, setTotalCostRaw] = useState(0);

  const [dataFetched, setDataFetched] = useState([]);
  const [pricingPN, setPricingWithPN] = useState<PricingModel>();
  const [fetchingError, setFetchingError] = useState({});

  const calculateListPrices = (prices: PricingModel) => {
    setM365ListPrice(
      storage ? prices.D09S8ZX * DISCOUNTS : prices.D09S9ZX * DISCOUNTS,
    );
    setEntraListPrice(prices.D0EQHZX * DISCOUNTS);
    setSfListPrice(
      storage ? prices.D0BY8ZX * DISCOUNTS : prices.D0BYAZX * DISCOUNTS,
    );
    setD365ListPrice(
      storage ? prices.D0DGXZX * DISCOUNTS : prices.D0DGZZX * DISCOUNTS,
    );
    setGwListPrice(
      storage ? prices.D0HDUZX * DISCOUNTS : prices.D0HDWZX * DISCOUNTS,
    );
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const priceArray = products.map((item) => ({
        [item.partNumber]:
          item.pricingIndicator === 'YR'
            ? item.scaleQtyPrice[0].price / 12
            : item.pricingIndicator === 'MN'
            ? item.scaleQtyPrice[0].price
            : 0,
      }));
      const priceObj = Object.assign({}, ...priceArray);
      calculateListPrices(priceObj);
      setPricingWithPN(priceObj);
    }
  };

  const fetchPrices = () => {
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const calculatePrice = (price: number, qty: number) => {
    return (
      price * qty * (expectional !== undefined ? expectional.conversion : 1)
    );
  };

  useEffect(() => {
    if (currency && country && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [localeCode, currency, country]);

  useEffect(() => {
    if (pricingPN) {
      calculateListPrices(pricingPN);
    }
  }, [storage]);

  useEffect(() => {
    const m365FP = calculatePrice(m365, m365ListPrice);
    const entraFP = calculatePrice(entra, entraListPrice);
    const salesforceFP = calculatePrice(salesforce, sfListPrice);
    const d365FP = calculatePrice(d365, d365ListPrice);
    const gwFP = calculatePrice(gw, gwListPrice);

    setM365Price(m365FP);
    setEntraPrice(entraFP);
    setSFPrice(salesforceFP);
    setD365(d365FP);
    setGWPrice(gwFP);

    setTotalCost(m365FP + entraFP + salesforceFP + d365FP + gwFP);
    setTotalCostRaw(m365FP + entraFP + salesforceFP + d365FP + gwFP);
  }, [
    m365,
    entra,
    salesforce,
    d365,
    gw,
    m365ListPrice,
    entraListPrice,
    sfListPrice,
    d365ListPrice,
    gwListPrice,
  ]);

  return [
    totalCost,
    m365Price,
    entraPrice,
    sfPrice,
    d365Price,
    gwPrice,
    totalCostRaw,
  ];
};
